// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMUrlUtilService, NameValueMap } from '@services/utils/lm-url-util.service';
import { HttpParamEncoderService } from '@services/utils/http.param.encoder.service';
import { Observable, Observer, of, Subject, throwError, timer } from 'rxjs';
import { catchError, takeUntil, map } from 'rxjs/operators';
import { LMContextService } from '@services/lm-context.service';
import { LMErrorUtilService, LMListFilterPersistenceService, LMRouteComponentService } from '@app/services';
import { LMListUtilService } from './lm-list-util.service';
import {AppConstants, LMGlobalUtilService, LMUniqueIdService} from '@app/utils';
import { LMBaseComponent, LMListComponent } from '@app/components';

@Injectable()
export class LMListGridPagingService {

  constructor(
    private http: HttpClient,
    private lmContextService: LMContextService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmListFilterPersistenceService: LMListFilterPersistenceService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmRouteComponentService: LMRouteComponentService,
    private lmListUtilService: LMListUtilService) {
  }

  /**
   * Retrieves an initial page of data for both root lists and related lists.
   * todo: need a type for the result of a paging operation
   * @param url - The url used to retrieve the initial set of records. Usually LMListSpec.initialDataUrl
   * @param pageSize - The page size to updated in the url
   * @param postContent - any content that needs to be posted - fromDataView when `selecting` for example.
   * @param isManualLoad - indicates to return the dataViewSet without getting the first page of records.
   * @returns return the json object containing the result.
   * @deprecated use initialPageFiltered instead.
   */
  initialPage(url: string, pageSize: number, postContent?: any, columnarOptions?: LMColumnarOptions, isManualLoad?: boolean): Observable<LMListPagingResponse> {
    const urlParams: LMUrlParams = { pagesize: pageSize };
    if (columnarOptions && columnarOptions.selectedDimension) {
      urlParams['sortOrderName'] = columnarOptions.selectedDimension;
      urlParams['isAscending'] = columnarOptions.isAscending;
    }
    if (isManualLoad) {
      urlParams['isManualLoad'] = true;
    }
    url = this.lmUrlUtilService.updateUrlParams(url, urlParams);
    return this.page(url, undefined, postContent, 'load');
  }

  initialPageFiltered(url: string, filterCriteria: Array<LMFilterFieldCriteria>, pageSize: number, postContent?: any, isManualLoad?: boolean, columnarOptions?: LMColumnarOptions, sortOrder?: LMListSortOrder): Observable<LMListPagingResponse> {
    const urlParams: LMUrlParams = { pagesize: pageSize };
    // a user can click sort before data is initially retrieved on a use as search list,
    // make sure to include sort order info
    if (sortOrder) {
      urlParams['sortOrderName'] = sortOrder.sortOrderName;
      urlParams['isAscending'] = sortOrder.isAscending;
    }
    if (columnarOptions && columnarOptions.selectedDimension) {
      urlParams['sortOrderName'] = columnarOptions.selectedDimension;
      urlParams['isAscending'] = columnarOptions.isAscending;
    }
    url = this.lmUrlUtilService.updateUrlParams(url, urlParams);
    filterCriteria = filterCriteria && this.lmListUtilService.ignoreStickyOperator(filterCriteria);
    if (filterCriteria && filterCriteria.length > 0) {
      url += '&' + this.buildFilterCriteriaQueryString(filterCriteria);
    }
    if (isManualLoad) {
      const urlParams: LMUrlParams = { isManualLoad: true };
      url = this.lmUrlUtilService.updateUrlParams(url, urlParams);
    }
    return this.page(url, undefined, postContent, 'load');
  }

  /**
   * Get sorted dataViewSet given a sortOrderName and direction.
   * @param dataViewSet - The dataViewSet you want to sort
   * @param sortOrderName - The sortOrderName to sort by
   * @param sortAscending - Whether to sort ascending or descending
   * @param postContent - any relavent post content. SelectListData for example.
   * @returns
   */
  sortPage(
    url: string,
    filterCriteria: Array<LMFilterFieldCriteria>,
    sortOrder: LMListSortOrder,
    pageSize: number,
    postContent?: any,
    cubeOptions?: LMCubeUrlParams,
    selectRequestData?: SelectRequestData
  ): Observable<LMListPagingResponse> {
    url = this.lmUrlUtilService.updateUrlParams(url, {
      pageop: 'sort',
      pagesize: pageSize,
      sortOrderName: sortOrder.sortOrderName,
      isAscending: sortOrder.isAscending
    });
    filterCriteria = filterCriteria && this.lmListUtilService.ignoreStickyOperator(filterCriteria);
    if (filterCriteria && filterCriteria.length > 0) {
      url += '&' + this.buildFilterCriteriaQueryString(filterCriteria);
    }
    url = (cubeOptions) ? url + '&' + this.lmUrlUtilService.objectToCubeQueryString(cubeOptions)
      : url;
    if (selectRequestData?.onSearchForm){
      url = this.lmUrlUtilService.addParamToUrl(url, 'searchform', 'true');
    }
    return this.page(url, undefined, postContent, 'sort');
  }

  /**
   *
   */
  filterPage(url: string, filterCriteria: Array<LMFilterFieldCriteria>, pageSize: number, postContent?: any, sortOrderCriteria?: LMListSortOrder, cubeOptions?: LMCubeUrlParams,
    columnarOptions?: LMColumnarOptions): Observable<LMListPagingResponse> {
    const urlParams: LMUrlParams = { pagesize: pageSize, pageop: 'filter' };
    if (sortOrderCriteria) {
      urlParams['sortOrderName'] = sortOrderCriteria.sortOrderName;
      urlParams['isAscending'] = sortOrderCriteria.isAscending;
    }
    if (columnarOptions && columnarOptions.selectedDimension) {
      urlParams['sortOrderName'] = columnarOptions.selectedDimension;
      urlParams['isAscending'] = columnarOptions.isAscending;
    }
    url = this.lmUrlUtilService.updateUrlParams(url, urlParams);
    url = (cubeOptions) ? url + '&' + this.lmUrlUtilService.objectToCubeQueryString(cubeOptions)
      : url;
    const requestParams = this.buildFilterCriteriaQueryStringMap(this.lmListUtilService.ignoreStickyOperator(filterCriteria));
    return this.page(url, requestParams, postContent, 'filter');
  }

  reloadPage(dataViewSet: LMDataViewSet, newPageSize: number, postContent?: any, cubeOptions?: LMCubeUrlParams, aggregation?: string, selectRequestData?: SelectRequestData, reportAsOfDate?:string): Observable<LMListPagingResponse> {
    const url = this.buildReloadUrl(dataViewSet, newPageSize, cubeOptions, aggregation, selectRequestData, reportAsOfDate);
    return this.page(url, undefined, postContent, 'load');
  }

  buildReloadUrl(dataViewSet: LMDataViewSet, newPageSize: number, cubeOptions?: LMCubeUrlParams, aggregation?: string, selectRequestData?: SelectRequestData, reportAsOfDate?:string): string {

    let params = { pagesize: newPageSize };
    if (aggregation !== undefined) {
      params = Object.assign(params, { aggregation });
    }
    let url = this.lmUrlUtilService.updateUrlParams(dataViewSet.pagingUrls.reloadPageUrl, params);
    url = (cubeOptions) ? url + '&' + this.lmUrlUtilService.objectToCubeQueryString(cubeOptions)
        : url;
    url = (aggregation) ? this.lmUrlUtilService.appendToQueryString(url, 'aggregation=' + aggregation) : url;
    if (selectRequestData?.onSearchForm) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'searchform', 'true');
    }

    if(reportAsOfDate) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'reportAsOfDate', reportAsOfDate);
    }
    return url;
  }

  reloadPage2(url: string, postContent?: any): Observable<LMListPagingResponse> {
    return this.page(url, undefined, postContent, 'load');
  }

  /**
   *
   * @param dataViewSet
   * @param newPageSize
   * @returns
   */
  firstPage(dataViewSet: LMDataViewSet, newPageSize: number, postContent?: any, cubeOptions?: LMCubeUrlParams, selectRequestData?: SelectRequestData, reportAsOfDate?: string): Observable<LMListPagingResponse> {
    let url = this.lmUrlUtilService.updateUrlParams(dataViewSet.pagingUrls.firstPageUrl, { pagesize: newPageSize });
    url = (cubeOptions) ? url + '&' + this.lmUrlUtilService.objectToCubeQueryString(cubeOptions)
      : url;
    if (selectRequestData?.onSearchForm){
      url = this.lmUrlUtilService.addParamToUrl(url, 'searchform', 'true');
    }
    if (!!dataViewSet.recordCount) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'recordCount', dataViewSet.recordCount.toString());
    }
    if(reportAsOfDate) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'reportAsOfDate', reportAsOfDate);
    }
    return this.page(url, undefined, postContent, 'first');
  }

  /**
   *
   * @param dataViewSet
   * @returns
   */
  lastPage(dataViewSet: LMDataViewSet, postContent?: any, cubeOptions?: LMCubeUrlParams, selectRequestData?: SelectRequestData, reportAsOfDate?: string ): Observable<LMListPagingResponse> {
    let url = (cubeOptions) ? dataViewSet.pagingUrls.lastPageUrl + '&' + this.lmUrlUtilService.objectToCubeQueryString(cubeOptions)
      : dataViewSet.pagingUrls.lastPageUrl;
    if (selectRequestData?.onSearchForm){
      url = this.lmUrlUtilService.addParamToUrl(url, 'searchform', 'true');
    }
    if (!!dataViewSet.recordCount) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'recordCount', dataViewSet.recordCount.toString());
    }
    if(reportAsOfDate) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'reportAsOfDate', reportAsOfDate);
    }
    return this.page(url, undefined, postContent, 'last');
  }

  /**
   *
   * @param dataViewSet
   * @returns
   */
  nextPage(dataViewSet: LMDataViewSet, postContent?: any, cubeOptions?: LMCubeUrlParams, selectRequestData?: SelectRequestData, reportAsOfDate?: string): Observable<LMListPagingResponse> {
    let url = (cubeOptions) ? dataViewSet.pagingUrls.nextPageUrl + '&' + this.lmUrlUtilService.objectToCubeQueryString(cubeOptions)
      : dataViewSet.pagingUrls.nextPageUrl;
    if (selectRequestData?.onSearchForm){
      url = this.lmUrlUtilService.addParamToUrl(url, 'searchform', 'true');
    }
    if (!!dataViewSet.recordCount) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'recordCount', dataViewSet.recordCount.toString());
    }
    if(reportAsOfDate) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'reportAsOfDate', reportAsOfDate);
    }
    return this.page(url, undefined, postContent, 'next');
  }

  /**
   *
   * @param dataViewSet
   * @returns
   */
  previousPage(dataViewSet: LMDataViewSet, postContent?: any, cubeOptions?: LMCubeUrlParams, selectRequestData?: SelectRequestData, reportAsOfDate?: string): Observable<LMListPagingResponse> {
    let url = (cubeOptions) ? dataViewSet.pagingUrls.previousPageUrl + '&' + this.lmUrlUtilService.objectToCubeQueryString(cubeOptions)
      : dataViewSet.pagingUrls.previousPageUrl;
    if (selectRequestData?.onSearchForm){
      url = this.lmUrlUtilService.addParamToUrl(url, 'searchform', 'true');
    }
    if (!!dataViewSet.recordCount) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'recordCount', dataViewSet.recordCount.toString());
    }

    if(reportAsOfDate) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'reportAsOfDate', reportAsOfDate);
    }
    return this.page(url, undefined, postContent, 'previous');
  }

  private page(url: string, requestParams?: any, postContent?: any, pageOp?: LMPageOp): Observable<LMListPagingResponse> {
    // console.log('Paging: ' + url.substring(0, url.indexOf('?')));
    if (this.lmContextService.isUiPagingRequestWaitTimeEnabled) {
      const pagingId = LMUniqueIdService.generateUUID();
      return new Observable<LMListPagingResponse>(observer => {
        const cancelSubject$ = new Subject<string>();
        cancelSubject$.subscribe(cancelMessage => {
          // listening to see if lm-list.component or one of the list implementations (grid. tree, etc) cancels the request
          const busClassName = cancellableNotifier.getBusClassName();
          const listName = cancellableNotifier.getListName();

          if (!this.lmContextService.isDisableWebRequestInterrupt()) {
            let headers: HttpHeaders = new HttpHeaders();
            headers = headers.append('busClassName', busClassName);
            headers = headers.append('listName', listName);
            headers = headers.append('interruptId', pagingId);
            this.lmGlobalUtilService.sendInterruptThreads(headers).subscribe();
          }

          const jsonLog = {
            message: cancelMessage,
            pagingUrl: url,
          };
          this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
          observer.next({ status: 'CANCELED' });
          observer.complete();
        });

        const wait = this.lmContextService.uiPagingRequestWaitTime;
        const cancellableNotifier = new LMCancellableNotifier(observer, cancelSubject$, wait);
        const keyedResource: KeyedResource = this.lmUrlUtilService.getRouteResource(url);
        const busClassName = keyedResource.busClassName;
        const listName = keyedResource.uiResourceName;
        cancellableNotifier.setBusClassName(busClassName);
        cancellableNotifier.setListName(listName);
        cancellableNotifier.setPagingId(pagingId);
        cancellableNotifier.start();
        this.makeCancellableHttpCall(cancelSubject$, url, requestParams, postContent, pagingId)
            .subscribe({
            next: (response: LMListPagingResponse) => {
                cancellableNotifier.finish();
                if (pageOp !== undefined) {
                  response.pageOp = pageOp;
                }
                observer.next(response);
                observer.complete();
            },
            error: (err) => {
              observer.next(err);
              observer.complete();
              cancellableNotifier.finish();
            }
        });
      });
    } else {
      return this.makeHttpCall(url, requestParams, postContent, pageOp);
    }
  }

  private makeHttpCall(url: string, requestParams?: any, postContent?: any, pageOp?: LMPageOp ): Observable<LMListPagingResponse> {
    // Filter parameters are added to HttpParams. If the filter string is appended to the url
    // it does not remain utf-8 encoded when it reaches the server.

    // Testing code to simulate a 504 - request timout error
    // if (url.includes('pagesize=200')) {
    //   url = 'https://httpstat.us/504?sleep=5000';
    // }

    let params = new HttpParams({ encoder: new HttpParamEncoderService() });
    if (requestParams) {
      Object.keys(requestParams).forEach((key: string) => params = params.set(key, requestParams[key]));
    }
    if (postContent !== undefined) {
      return this.http.post<LMListPagingResponse>(url,  postContent, { params }).pipe(
          map(response => {
            response.pageOp = pageOp;
            return response;
          }),
          catchError(error => {
            this.lmLoggerService.error(error);
            return throwError(error || 'Server error');
          })
      );
    } else {
      return this.http.get<LMListPagingResponse>(url, { params }).pipe(
          map(response => {
            response.pageOp = pageOp;
            return response;
          }),
          catchError(error => {
            this.lmLoggerService.error(error);
            return throwError(error || 'Server error');
          })
      );
    }
  }

  private makeCancellableHttpCall(cancelSubject$: Subject<string>, url: string, requestParams?: any, postContent?: any, pagingId?: string): Observable<LMListPagingResponse> {
    // Filter parameters are added to HttpParams. If the filter string is appended to the url
    // it does not remain utf-8 encoded when it reaches the server.
    let params = new HttpParams({ encoder: new HttpParamEncoderService()});
    let headers: HttpHeaders = new HttpHeaders();
    // Add the pagingId to the header for all paging calls made
    if (pagingId) {
        headers = headers.append('pagingId', pagingId);
    }

    if (requestParams) {
      Object.keys(requestParams).forEach((key: string) => params = params.set(key, requestParams[key]));
    }
    if (postContent !== undefined) {
      return <Observable<LMListPagingResponse>> this.http.post(url, postContent, { params, headers }).pipe(takeUntil(cancelSubject$),  catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
    } else {
      return <Observable<LMListPagingResponse>> this.http.get(url, { params, headers }).pipe(takeUntil(cancelSubject$), catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
    }
  }

  private buildFilterCriteriaQueryString(filterCriteria: Array<LMFilterFieldCriteria>): string {
    // set filter criteria on the url params
    if (filterCriteria) {
      const filterParamString = this.lmUrlUtilService.filterFieldCriteriaToUrlParams(filterCriteria, 'fc');
      if (filterParamString) {
        return filterParamString;
      }
    }
    return '';
  }

  private buildFilterCriteriaQueryStringMap(filterCriteria: Array<LMFilterFieldCriteria>): any {
    // set filter criteria on the url params
    let filterParams = {};
    if (filterCriteria) {
      const filterParamMap = this.lmUrlUtilService.filterFieldCriteriaToNameValueMap(filterCriteria, 'fc');
      if (filterParamMap) {
        filterParamMap.forEach((f: NameValueMap) => {
          filterParams = { ...filterParams, ...f };
        });
      }
    }
    return filterParams;
  }

  buildPostContent(listUrlInfo: LMListUrlInfo, selectRequestData?: SelectRequestData): FilterPreviewRequest | SelectPagingRequest {
    if (selectRequestData) {
      return this.lmUrlUtilService.buildSelectDataViewPagingRequest(selectRequestData);
    }
    if (listUrlInfo.filterId) {
      const query = this.lmListFilterPersistenceService.getFilterItem(listUrlInfo.filterId);
      if(!query) {
        // a null query means that the filter is expired
        listUrlInfo.filterId = null;
        this.lmErrorUtilService.handleError(Soho.Locale.translate('FilterExpiredMessage'));
      }
      return this.lmUrlUtilService.buildFilterPreviewRequest(query);
    }
    return undefined;
  }
}

export class LMCancellableNotifier {
  private clock: Observable<number>;
  private finished = false;
  private observer: Observer<LMListPagingResponse>;
  private cancelSubject$: Subject<string>;
  private interval: number;
  private busClassName: string;
  private listName: string;
  private pagingId: string;

  constructor(observer: Observer<LMListPagingResponse>, cancelSubject$: Subject<string>, interval: number) {
    this.observer = observer;
    this.cancelSubject$ = cancelSubject$;
    this.interval = interval;
  }

  public start() {
    this.observer.next({ status: 'STARTED', cancelablePagingNotifier: this.cancelSubject$ });
    this.setTimer();
  }

  public finish() {
    this.finished = true;
  }

  private setTimer() {
    this.clock = timer(this.interval);
    this.clock.subscribe(x => {
      if (this.finished) {
        return;
      }

      this.observer.next({ status: 'WAITING' });
      this.setTimer();
    });
  }

  public getBusClassName(): string {
    return this.busClassName;
  }

  public setBusClassName(busClassName: string) {
    this.busClassName = busClassName;
  }

  public getListName(): string {
    return this.listName;
  }

  public setListName(listName: string) {
    this.listName = listName;
  }

  public getPagingId(): string {
    return this.pagingId;
  }

  public setPagingId(pagingId: string) {
    this.pagingId = pagingId;
  }

}
